// src/app/services/socket.service.ts
import { Injectable, signal, inject, OnDestroy, effect } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, of, fromEvent, Subject, BehaviorSubject, Subscription, firstValueFrom, timeout } from 'rxjs';
import { NotificationSnackComponent } from '../shared/notification-snack/notification-snack.component';
import { io, Socket } from 'socket.io-client';
import { authService } from './auth.service';
import { ApiBaseService } from './api.base.service';
import { ImportJobService } from './importJobService';
import { activityTypeCache } from './cache/activity-types-cache';
import { CampusCacheService } from './cache/campus-cache';
import { UsersCacheService } from './cache/users-cache';
import { TagsCacheService } from './cache/tags-cache';
import { NotificationSettingsService } from './notification-settings.service';

@Injectable({
  providedIn: 'root',
})
export class SocketService extends ApiBaseService implements OnDestroy {
  private socket: Socket | null = null;
  private _connected = signal(false);
  private _total_notifications = signal(0);
  private _total_inbox_messages = signal(0);
  private audio: HTMLAudioElement;
  
  // Remove all custom reconnection logic
  private notificationSubject = new Subject<any>();
  private bulkUpdateSubject = new Subject<any>();
  private inboxSubject = new Subject<any>();
  private authStateSubscription: Subscription;
  
  // ========== ADD: Lead update subject for real-time lead updates ==========
  private leadUpdateSubject = new Subject<any>();
  // =========================================================================
  
  // Track page visibility to manage connection efficiently
  private isPageVisible = true;
  private wasConnectedBeforeHidden = false;

  private _messageStatus$ = new Subject<{
    jobId: string;
    status: 'sending' | 'sent' | 'failed';
    uniqueId?: string;
    leadId?: number;
    messageRecord?: any;
    error?: string;
  }>();
  
  private newMessageSubject = new Subject<any>();

  // ADD these getter methods to your SocketService class

  get messageStatus$(): Observable<any> {
    return this._messageStatus$.asObservable();
  }

  getNewMessages(): Observable<any> {
    return this.newMessageSubject.asObservable();
  }

  // ========== ADD: Getter for lead updates ==========
  getLeadUpdates(): Observable<any> {
    return this.leadUpdateSubject.asObservable();
  }
  // ==================================================

  /**
   * Get the current socket ID if socket is connected
   * @returns socket.id or undefined if not connected
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  constructor(
    private snackBar: MatSnackBar,
    public http: HttpClient,
    private authTokenService: authService,
    private importJobService: ImportJobService,
    private activityTypeCache: activityTypeCache,
    private campusCacheService: CampusCacheService,
    private usersCacheService: UsersCacheService,
    private TagsCacheService: TagsCacheService,
    private notificationSettings: NotificationSettingsService
  ) {
    super(http, '/notifications');
    this.audio = new Audio('assets/audio/notification.mp3');
    this.audio.volume = this.notificationSettings.getVolume(); // Default 10%

    // React to volume changes
    effect(() => {
      const settings = this.notificationSettings.settings();
      this.audio.volume = settings.volume;
    });

    // Only register service worker and initialize socket if feature is enabled
    if (environment.useSocketNotifications === true) {
      // Subscribe to auth state changes
      this.authStateSubscription = this.authTokenService.authState$.subscribe(isAuthenticated => {
        if (isAuthenticated && !this.socket) {
          // Small delay to ensure token is fully saved
          setTimeout(() => {
            this.initSocketConnection();
          }, 500);
        } else if (!isAuthenticated && this.socket) {
          this.disconnect();
        }
      });
      
      // this.setupServiceWorkerCommunication();
      this.setupPageVisibilityHandling();
      this.setupNetworkHandling();
      
      // Only init if already authenticated
      if (this.authTokenService.isAuthenticated()) {
        this.initSocketConnection();
      }
    }
  }

 

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }

    // Disconnect socket
    this.disconnect();

    // Complete subjects
    this.notificationSubject.complete();
    this.inboxSubject.complete();
    this.leadUpdateSubject.complete(); // ADD THIS

    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  /**
   * Setup page visibility handling for efficient connection management
   */
  private setupPageVisibilityHandling(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Setup network state handling
   */
  private setupNetworkHandling(): void {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  private handleVisibilityChange = () => {
    this.isPageVisible = document.visibilityState === 'visible';

    if (!this.socket) return;

    if (document.visibilityState === 'hidden') {
      this.wasConnectedBeforeHidden = this.socket.connected;
    } else if (document.visibilityState === 'visible') {
      // Page is visible again - reconnect if we were connected before
      if (this.wasConnectedBeforeHidden && !this.socket.connected && this.authTokenService.isAuthenticated()) {
        this.socket.connect();
      }
    }
  };

  /**
 * Network came back – (make sure we have a valid token) → connect
 */
private handleOnline = async () => {
  // 1. Make sure we have a socket instance
  if (!this.socket) {
    // reuse existing manager if you want
    this.initSocketConnection();
    return;
  }

  // 2. Get (or refresh) the token unconditionally
  let token = this.authTokenService.getToken();

  if (!token || this.authTokenService.isTokenExpired(token)) {
    try {
      token = await firstValueFrom(
        this.authTokenService.onGetNewAccessToken().pipe(timeout(10000))
      );
    } catch (err) {
      console.error('Token refresh failed, giving up socket reconnect:', err);
      return;                       // user will be forced to log in again
    }
  }

  // 3. Update socket auth + connect
  this.socket.auth = { token };
  if (!this.socket.connected) {
    this.socket.connect();
  }
};

  private handleOffline = () => {
    // Track connection state but don't call close() — calling close()
    // tells Socket.io the disconnect was intentional and kills auto-reconnect.
    // Let Socket.io detect the network drop and auto-reconnect on its own.
    this.wasConnectedBeforeHidden = this.socket?.connected || false;
  };

  /**
   * Initialize socket connection with robust configuration
   */
  private initSocketConnection(): void {
    const token = this.authTokenService.getToken();
    
    // Validate token before connecting
    if (!token || this.authTokenService.isTokenExpired(token)) {
      console.warn('Invalid or expired token, skipping socket connection');
      return;
    }

    try {
      // Close existing connection if any
      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
      }

      // Create new connection with robust reconnection settings
      this.socket = io(environment.socketUrl, {
        autoConnect: true,
        auth: { token },
        
        // Transport configuration
        transports: ['websocket', 'polling'], // Fallback to polling if websocket fails
        upgrade: true,
        timeout: 8000, // Connection timeout
        
        // Robust reconnection configuration
        reconnection: true,
        reconnectionAttempts: Infinity, // Never give up trying to reconnect
        reconnectionDelay: 2000, // Start with 2 second delay
        reconnectionDelayMax: 10000, // Cap at 10 seconds
        randomizationFactor: 0.3, // Add randomization to prevent thundering herd
        
        // Additional options for stability
        forceNew: false, // Reuse existing connection manager when possible
      });

      this.setupSocketEvents();
    } catch (error) {
      console.error('Error initializing socket:', error);
    }
  }

  /**
   * Set up socket event listeners with robust error handling
   */
  private setupSocketEvents(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this._connected.set(true);
      
      // Authenticate immediately upon connection
      this.authenticate();
    });

    this.socket.on('disconnect', (reason) => {
      this._connected.set(false);
      
      // Socket.IO will handle reconnection automatically
      // No need for custom reconnection logic
    });

    // ========== ADD: Listen for lead updates ==========
    this.socket.on('lead-update', (data) => {
      console.log('📥 Lead update received:', data.type, data);
      this.leadUpdateSubject.next(data);
    });
    // ==================================================

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this._connected.set(false);
      
      // Handle authentication errors specifically
      if (error.message?.includes('unauthorized') || 
          error.message?.includes('jwt') || 
          error.message?.includes('token')) {
        this.handleAuthError();
      }
      // For other errors, let Socket.IO handle reconnection
    });

    // Reconnection events for debugging
    this.socket.io.on('reconnect', (attemptNumber) => {
    });

    this.socket.io.on('reconnect_attempt', (attemptNumber) => {
    });

    this.socket.io.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
    });

    this.socket.io.on('reconnect_failed', () => {
      console.error('Socket reconnection failed - all attempts exhausted');
      this.showReconnectFailedNotification();
    });

    // Transport events for debugging
    this.socket.io.engine.on('upgrade', (transport) => {
    });

    this.socket.io.engine.on('upgradeError', (err) => {
      console.error('Socket transport upgrade error:', err);
    });

    // Notification events
    this.socket.on('notification', (data) => {
      this.handleIncomingNotification(data);
    });

    this.socket.on('notification_count', (data) => {
      this.set(data.unseenCount);
    });

    this.socket.on('inbox_count', (data) => {
      this.setInboxCount(data.unseenCount);
    });

    this.socket.on('lead_notification', (data) => {
      this.notificationSubject.next(data);
    });

    this.socket.on('message-status', (payload) => {
      this._messageStatus$.next(payload);
    });

    // Listen for new messages in lead conversations
    this.socket.on('new-message', (data) => {
      this.newMessageSubject.next(data);
    });

    // Handle server-side errors
    this.socket.on('error', (error) => {
      console.error('Socket server error:', error);
      if (error.message?.includes('Rate limit exceeded')) {
        this.showRateLimitNotification();
      }
    });
  }

  /**
   * Handle incoming notifications with proper routing
   */
  // Update handleIncomingNotification method
private async handleIncomingNotification(data: any): Promise<void> {
  const messageData = data;
  const notificationType = data.type || data.notification?.type;
  console.log(data, 'notification data');
  
  // Route to appropriate handler based on notification type
  switch (notificationType) {
    case 'import_job':
      this.handleImportJob(messageData);
      break;
    case 'activity-type':
      await this.handleEntityChanges({ data: { data: messageData.data, notification: messageData }}, 
        this.activityTypeCache, 'activityTypesObj');
      break;
    case 'campuses':
      await this.handleEntityChanges({ data: { data: messageData.data, notification: messageData }}, 
        this.campusCacheService, 'campusObj');
      break;
    case 'users':
      if (messageData.method === 'post') {
        const userData = JSON.parse(messageData?.data?.userObj);
        const { Company, ...userWithoutCompany } = userData;
        await this.usersCacheService.createLocal(userWithoutCompany);
      } else {
        await this.handleEntityChanges({ data: { data: messageData.data, notification: messageData }}, 
          this.usersCacheService, 'userObj');
      }
      break;
    case 'tags':
      await this.handleEntityChanges({ data: { data: messageData.data, notification: messageData }}, 
        this.TagsCacheService, 'tagsObj');
      break;
    
    // ADD THIS CASE for bulk updates
    case 'Bulk update success':
    case 'Bulk update partial':
    case 'Bulk update failed':
      console.log('📦 Bulk update notification received:', notificationType);
      this.bulkUpdateSubject.next({
        type: notificationType,
        notification: data.notification || data
      });
      this.handleNotification(data);
      break;
      
    default:
      this.handleNotification(data);
  }
}

  /**
   * Show notification when reconnection fails completely
   */
  private showReconnectFailedNotification(): void {
    this.snackBar.open(
      'Lost connection to server. Please check your internet connection and refresh the page.',
      'Refresh',
      { 
        duration: 0, // Don't auto-dismiss
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: 'error-snackbar'
      }
    ).onAction().subscribe(() => {
      window.location.reload();
    });
  }

  /**
   * Show rate limit notification
   */
  private showRateLimitNotification(): void {
    this.snackBar.open(
      'Connection rate limited. Please wait a moment before trying again.',
      'OK',
      { 
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: 'warning-snackbar'
      }
    );
  }

  /**
   * Handle authentication errors by refreshing token
   */
  private handleAuthError(): void {
    // Try to refresh token
    this.authTokenService.onGetNewAccessToken().subscribe({
      next: (newToken) => {
        if (this.socket) {
          this.socket.auth = { token: newToken };
          // Let Socket.IO handle the reconnection
          if (!this.socket.connected) {
            this.socket.connect();
          }
        }
      },
      error: (error) => {
        console.error('Failed to refresh token:', error);
        this.disconnect();
        // Potentially redirect to login
      }
    });
  }

  /**
   * Authenticate the socket connection
   */
   authenticate(): void {
    if (!this.socket || !this.socket.connected) return;

    const token = this.authTokenService.getToken();
    if (!token || this.authTokenService.isTokenExpired(token)) {
      console.warn('Token expired during socket authentication');
      this.handleAuthError();
      return;
    }

    this.socket.emit('authenticate', { token }, (response) => {
      if (response?.status === 'success') {
        // Subscribe to leads after authentication if needed
        // this.subscribeToFollowedLeads();
      } else {
        console.error('Socket authentication failed:', response);
        // Socket.IO will handle reconnection
      }
    });
  }

  /**
   * Set up communication with the service worker
   */
  private setupServiceWorkerCommunication(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/socket-messaging-sw.js').then(
        (registration) => {
        },
        (error) => {
          console.error('Socket.IO Service Worker registration failed:', error);
        }
      );

      // Listen for messages from the service worker
      navigator.serviceWorker.addEventListener('message', async (event) => {
        if (event.data?.type === "NO_NEW_VERSION_DETECTED" || 
            event.data?.payload?.type === "NO_NEW_VERSION_DETECTED") {
          return;
        }
        if (event.data?.version && !event.data?.version?.appData) {
          return;
        }
        if (!event.data ||
            typeof event.data !== 'object' ||
            !event.data.data ||
            !event.data.notification) {
          return;
        }

        if (event.data?.type === 'SOCKET_BACKGROUND_NOTIFICATION') {
          this.handleBackgroundNotification(event.data.notification);
          return;
        }

        const messageData = event.data.payload || event.data;
        const notificationType = messageData.notification?.type;
      
        // Route to appropriate handler based on notification type
        switch (notificationType) {
          case 'import_job':
            await this.handleImportJob(messageData); // Add await
            break;
          case 'activity-type':
            await this.handleEntityChanges(event, this.activityTypeCache, 'activityTypesObj');
            break;
          case 'campuses':
            await this.handleEntityChanges(event, this.campusCacheService, 'campusObj');
            break;
          case 'users':
            const userData = JSON.parse(event?.data?.data?.userObj);
            if (messageData.notification.method === 'post') {
              const { Company, ...userWithoutCompany } = userData;
              await this.usersCacheService.createLocal(userWithoutCompany); // Add await
            } else {
              await this.handleEntityChanges(event, this.usersCacheService, 'userObj');
            }
            break;
          case 'tags':
            await this.handleEntityChanges(event, this.TagsCacheService, 'tagsObj');
            break;
          default:
            this.handleNotification(event);
        }
      });
    }
  }


  async handleImportJob(messageData) {
    this.importJobService.updateJobStatus({
      jobId: messageData.data.jobId,
      progress: parseInt(messageData.data.progress, 10) || 0,
      successCount: parseInt(messageData.data.successCount, 10) || 0,
      failedCount: parseInt(messageData.data.failedCount, 10) || 0,
      status: messageData.data.status as 'in_progress' | 'success' | 'failed',
      message: messageData.notification.body
    });
  }

  private handleNotification(data: any): void {
    const notificationData = data.notification || data.payload || data;
    const isMessage = notificationData.type === 'Message';
    
    if (isMessage) {
      this.inboxSubject.next(data);
    } else {
      this.increment(1);
      this.notificationSubject.next(data);
    }
    
    this.playNotificationSound();
    
    this.snackBar.openFromComponent(NotificationSnackComponent, {
      duration: 10000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: {
        message: data.payload || data, 
        additionalInfo: isMessage ? 'New message received' : 'New notification',
      },
    });

    if (notificationData.id) {
      this.http.patch(
        `${environment.apiUrl}/notifications/markAsDelivered`,
        { notificationIds: [notificationData.id] }
      ).subscribe({
        error: (error) => console.error('Failed to mark notification as delivered:', error)
      });
    }
  }

  private handleBackgroundNotification(notification: any): void {
    const isMessage = notification.type === 'Message';
    
    if (isMessage) {
      this.incrementInbox(1);
    } else {
      this.increment(1);
    }
    
    this.playNotificationSound();
    
    this.snackBar.openFromComponent(NotificationSnackComponent, {
      duration: 10000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: {
        message: notification,
        additionalInfo: 'Background notification',
      },
    });
  }

  async handleEntityChanges(event, cacheService, dataKey) {
    const data = JSON.parse(event?.data?.data?.[dataKey])
    const notification = event?.data?.notification
    const method = notification.method
    switch (method) {
      case 'post':
      case 'reverse':
        await cacheService.createLocal(data)
        break
      case 'put':
        await cacheService.updateLocal(data, data.id)
        break
      case 'delete':
        await cacheService.deleteLocal(data.id)
        break
      case 'sequenceChange':
        if (cacheService.sequenceUpdate) {
          await cacheService.sequenceUpdate(data.previousIndex, data.currentIndex)
        }
        break
    }
  }

  /**
   * Subscribe to a specific lead for notifications
   */
  subscribeToLead(leadId: number): void {
    if (!this.socket || !this.isConnected()) {
      console.warn('Cannot subscribe to lead: socket not connected');
      return;
    }
    
    this.socket.emit('subscribe-to-lead', { leadId }, (response) => {
      if (response?.status === 'success') {
        console.log(`✅ Subscribed to lead ${leadId} for real-time updates`);
      } else {
        console.error(`Failed to subscribe to lead ${leadId}:`, response);
      }
    });
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get connection status as signal
   */
  connected() {
    return this._connected;
  }

  /**
   * Get notifications as observable
   */
  getNotifications(): Observable<any> {
    return this.notificationSubject.asObservable();
  }

  /**
   * Get inbox messages as observable
   */
  getInboxMessages(): Observable<any> {
    return this.inboxSubject.asObservable();
  }

  /**
   * Disconnect socket connection
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this._connected.set(false);
    }
  }

  /**
   * Manual reconnect (should rarely be needed)
   */
  reconnect(): void {
    if (this.socket && !this.socket.connected && this.authTokenService.isAuthenticated()) {
      this.socket.connect();
    } else if (!this.socket && this.authTokenService.isAuthenticated()) {
      this.initSocketConnection();
    }
  }

  private playNotificationSound(): void {
    // Check if sound is enabled before playing
    if (!this.notificationSettings.isSoundEnabled()) {
      return;
    }

    this.audio
      .play()
      .catch((err) => console.error('Error playing audio: ', err));
  }

  initializeAudio(): void {
    this.audio.load();
  }

  // API methods
  getTotalUnseen(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/notifications/unseen`);
  }

  getTotalUnseenInbox(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/notifications/unseenInbox`);
  }

  markAsSeen(data): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/notifications/markAsSeen`, data);
  }

  // Counter methods
  notification_count() {
    return this._total_notifications();
  }

  inbox_count() {
    return this._total_inbox_messages();
  }

  increment(value: number) {
    this._total_notifications.update(count => count + value);
  }

  incrementInbox(value: number) {
    this._total_inbox_messages.update(count => count + value);
  }

  decrement() {
    this._total_notifications.update(count => Math.max(0, count - 1));
  }

  decrementInbox() {
    this._total_inbox_messages.update(count => Math.max(0, count - 1));
  }

  set(value: number) {
    this._total_notifications.set(value);
  }

  setInboxCount(value: number) {
    this._total_inbox_messages.set(value);
  }

  getBulkUpdateEvents(): Observable<any> {
    return this.bulkUpdateSubject.asObservable();
  }

}